// frontend/src/components/ui/Filters.tsx
import * as React from "react";
import { useEffect, useState } from "react";
import { cities, modes } from "../../services/mockDataService";
import { CustomSelect } from "./custom-select";
import { DateInput } from "./dateInput";
import { useAuth } from "../../lib/authContext";
import { api } from "../../lib/api";

interface FiltersProps {
  selectedState: string;
  setSelectedState: (state: string) => void;
  selectedProvider: number;
  setSelectedProvider: (provider: number) => void;
  selectedMode: string;
  setSelectedMode: (mode: string) => void;
  startDate?: string;
  setStartDate?: (date: string) => void;
  endDate?: string;
  setEndDate?: (date: string) => void;
  showDateFilter?: boolean;
  selectedEntity: number;
  setSelectedEntity: (entity: number) => void;
  hidePharmacyFilter?: boolean;
}
interface Option {
  value: number | string;
  label: string;
}

export function Filters({
  selectedState,
  setSelectedState,
  selectedProvider,
  setSelectedProvider,
  selectedMode,
  setSelectedMode,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  showDateFilter = true,
  selectedEntity,
  setSelectedEntity,
  hidePharmacyFilter = false,
}: FiltersProps) {
  const { accessLevel, userEntities, userRole } = useAuth();
  const [providers, setProviders] = useState<Option[]>([]);
  const [states, setStates] = useState<Option[]>([]);
  const [entities, setEntities] = useState<Option[]>([]);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(true);

  useEffect(() => {
    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const fetchedProviders = await api.getFilters();
        if (fetchedProviders) {
          const formattedProviders: Option[] =
            fetchedProviders.data.providers.map((provider: any) => ({
              value: String(provider.id),
              label: provider.name,
            }));
          setProviders([
            { value: "0", label: "All Providers" },
            ...formattedProviders,
          ]);
          const formattedStates: Option[] = fetchedProviders.data.states.map(
            (state: any) => ({
              value: state.state,
              label: state.state,
            }),
          );
          setStates([
            { value: "all", label: "All States" },
            ...formattedStates,
          ]);
          const allEntities: Option[] =
            fetchedProviders.data.entities.map((entity: any) => ({
              value: entity.id,
              label: `${entity.name} (${entity.branch_id})`,
            }));

          const isSuperAdmin = userRole?.role_name === "Super Admin";

          let filteredEntities: Option[];

          if (isSuperAdmin && userEntities && userEntities.length > 0) {
            // Super Admin: use userEntities which now includes ALL entities (active + inactive)
            filteredEntities = userEntities.map((e: any) => ({
              value: e.entity_id,
              label: e.is_active === false
                ? `${e.name} (${e.branch_id}) [Inactive]`
                : `${e.name} (${e.branch_id})`,
            }));
          } else {
            // Other roles: filter from the active-only getFilters() list
            const allowedIds =
              userEntities && userEntities.length > 0
                ? new Set(userEntities.map((e: any) => e.entity_id))
                : null;
            filteredEntities = allowedIds
              ? allEntities.filter(e => allowedIds.has(Number(e.value)))
              : allEntities;
          }

          setEntities([
            { value: 0, label: "All Pharmacies" },
            ...filteredEntities,
          ]);
        }
      } catch (error) {
        console.error("Error fetching providers:", error);
      } finally {
        setLoadingProviders(false);
      }
    };

    fetchProviders();
  }, [userEntities, userRole]);

  // Auto-set provider when user has provider access.
  // Only fire when accessLevel changes AND the provider isn't already correctly set,
  // to prevent an infinite re-render loop caused by setSelectedProvider updating URL
  // params → re-render → new setSelectedProvider reference → effect fires again.
  useEffect(() => {
    if (accessLevel?.access_level?.provider_id && accessLevel.access_level.provider_id.length > 0) {
      const assignedProviderId = accessLevel.access_level.provider_id[0];
      if (Number(selectedProvider) !== Number(assignedProviderId)) {
        setSelectedProvider(assignedProviderId);
      }
    }
  }, [accessLevel]); // intentionally omit setSelectedProvider — it's a new reference each render

  return (
    <div className="flex flex-nowrap items-center space-x-4 overflow-x-auto pb-2 max-w-full">
      <CustomSelect
        options={states}
        value={selectedState}
        onChange={(value: string | number) => setSelectedState(String(value))}
        placeholder="Select State"
        className="w-[180px] flex-shrink-0"
        disabled={loadingProviders}
      />
      <CustomSelect
        options={
          accessLevel?.access_level?.provider_id && accessLevel.access_level.provider_id.length > 0
            ? providers.filter(p => p.value === accessLevel.access_level.provider_id![0].toString())
            : providers
        }
        value={selectedProvider.toString()}
        onChange={(value: string | number) => setSelectedProvider(Number(value))}
        placeholder="Select Provider"
        className="w-[180px] flex-shrink-0"
        disabled={loadingProviders || (accessLevel?.access_level?.provider_id && accessLevel.access_level.provider_id.length > 0)}
      />
      {!hidePharmacyFilter && (
        <CustomSelect
          options={entities}
          value={selectedEntity.toString()}
          onChange={(value: string | number) => setSelectedEntity(Number(value))}
          placeholder="Select Pharmacy"
          className="w-[200px] flex-shrink-0"
          disabled={loadingProviders}
        />
      )}
      <CustomSelect
        options={modes}
        value={selectedMode}
        onChange={(value: string | number) => setSelectedMode(String(value))}
        placeholder="Select Mode"
        className="w-[160px] flex-shrink-0"
        disabled={loadingProviders}
      />
      {showDateFilter &&
        startDate !== undefined &&
        endDate !== undefined &&
        setStartDate &&
        setEndDate && (
          <div className="flex items-center space-x-2 flex-shrink-0">
            <DateInput value={startDate} onChange={setStartDate} />
            <span className="dark:text-white">-</span>
            <DateInput value={endDate} onChange={setEndDate} />
          </div>
        )}
    </div>
  );
}
